import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Truck, Shield, ArrowRight } from 'lucide-react';
import { Layout } from '../components/Layout';
import { useCart } from '../context/CartContext';
import { createCheckoutSession, calculateShipping, getShippingConfig } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';

const countries = [
  { value: 'germany', label: 'Germany / Deutschland' },
  { value: 'austria', label: 'Austria / Österreich' },
  { value: 'switzerland', label: 'Switzerland / Schweiz' },
  { value: 'france', label: 'France' },
  { value: 'netherlands', label: 'Netherlands' },
  { value: 'belgium', label: 'Belgium' },
  { value: 'italy', label: 'Italy' },
  { value: 'spain', label: 'Spain' },
  { value: 'poland', label: 'Poland' },
  { value: 'czech republic', label: 'Czech Republic' },
  { value: 'denmark', label: 'Denmark' },
  { value: 'sweden', label: 'Sweden' },
  { value: 'finland', label: 'Finland' },
  { value: 'norway', label: 'Norway' },
  { value: 'uk', label: 'United Kingdom' },
  { value: 'ireland', label: 'Ireland' },
  { value: 'us', label: 'United States' },
  { value: 'canada', label: 'Canada' },
  { value: 'australia', label: 'Australia' },
  { value: 'other', label: 'Other' },
];

export const CheckoutPage = () => {
  const navigate = useNavigate();
  const { items, getCartTotal, clearCart } = useCart();
  const [loading, setLoading] = useState(false);
  const [shippingCost, setShippingCost] = useState(0);
  const [freeShippingThreshold, setFreeShippingThreshold] = useState(150);
  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    address: '',
    city: '',
    postalCode: '',
    country: '',
    phone: '',
  });

  const subtotal = getCartTotal();
  const total = subtotal + shippingCost;

  // Load shipping config on mount
  useEffect(() => {
    const loadConfig = async () => {
      try {
        const config = await getShippingConfig();
        setFreeShippingThreshold(config.free_shipping_threshold || 150);
      } catch (error) {
        console.error('Failed to load shipping config:', error);
      }
    };
    loadConfig();
  }, []);

  // Calculate shipping when country changes
  const updateShipping = useCallback(async (country) => {
    if (!country) {
      setShippingCost(0);
      return;
    }
    try {
      const result = await calculateShipping(country, subtotal);
      setShippingCost(result.shipping_cost);
    } catch (error) {
      console.error('Failed to calculate shipping:', error);
      // Default fallback
      setShippingCost(subtotal >= freeShippingThreshold ? 0 : 15);
    }
  }, [subtotal, freeShippingThreshold]);

  useEffect(() => {
    if (formData.country) {
      updateShipping(formData.country);
    }
  }, [formData.country, subtotal, updateShipping]);

  const handleInputChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleCountryChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      country: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.email || !formData.firstName || !formData.lastName) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (!formData.address || !formData.city || !formData.postalCode || !formData.country) {
      toast.error('Please complete your shipping address');
      return;
    }

    setLoading(true);

    try {
      const checkoutData = {
        items: items.map((item) => ({
          product_id: item.product_id,
          quantity: item.quantity,
          size: item.size,
          color: item.color,
        })),
        customer_email: formData.email,
        customer_name: `${formData.firstName} ${formData.lastName}`,
        shipping_address: {
          address: formData.address,
          city: formData.city,
          postal_code: formData.postalCode,
          country: formData.country,
          phone: formData.phone,
        },
        origin_url: window.location.origin,
      };

      const response = await createCheckoutSession(checkoutData);

      if (response.checkout_url) {
        // Redirect to Stripe
        window.location.href = response.checkout_url;
      } else {
        toast.error('Failed to create checkout session');
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error('An error occurred during checkout. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (items.length === 0) {
    navigate('/cart');
    return null;
  }

  return (
    <Layout>
      <section className="section-spacing" data-testid="checkout-page">
        <div className="container-luxury">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="font-heading text-3xl md:text-4xl text-[#2C2420] mb-12"
          >
            Checkout
          </motion.h1>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
            {/* Checkout Form */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <form onSubmit={handleSubmit} className="checkout-form space-y-8">
                {/* Contact */}
                <div>
                  <h2 className="text-xs uppercase tracking-widest text-[#8C8580] mb-6">
                    Contact Information
                  </h2>
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="email" className="text-sm text-[#5D5550]">
                        Email *
                      </Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        required
                        value={formData.email}
                        onChange={handleInputChange}
                        className="input-underline"
                        placeholder="your@email.com"
                        data-testid="checkout-email"
                      />
                    </div>
                    <div>
                      <Label htmlFor="phone" className="text-sm text-[#5D5550]">
                        Phone (optional)
                      </Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="input-underline"
                        placeholder="+49 123 456 7890"
                        data-testid="checkout-phone"
                      />
                    </div>
                  </div>
                </div>

                {/* Shipping */}
                <div>
                  <h2 className="text-xs uppercase tracking-widest text-[#8C8580] mb-6">
                    Shipping Address
                  </h2>
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="firstName" className="text-sm text-[#5D5550]">
                          First Name *
                        </Label>
                        <Input
                          id="firstName"
                          name="firstName"
                          required
                          value={formData.firstName}
                          onChange={handleInputChange}
                          className="input-underline"
                          data-testid="checkout-firstname"
                        />
                      </div>
                      <div>
                        <Label htmlFor="lastName" className="text-sm text-[#5D5550]">
                          Last Name *
                        </Label>
                        <Input
                          id="lastName"
                          name="lastName"
                          required
                          value={formData.lastName}
                          onChange={handleInputChange}
                          className="input-underline"
                          data-testid="checkout-lastname"
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="address" className="text-sm text-[#5D5550]">
                        Address *
                      </Label>
                      <Input
                        id="address"
                        name="address"
                        required
                        value={formData.address}
                        onChange={handleInputChange}
                        className="input-underline"
                        placeholder="Street address"
                        data-testid="checkout-address"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="city" className="text-sm text-[#5D5550]">
                          City *
                        </Label>
                        <Input
                          id="city"
                          name="city"
                          required
                          value={formData.city}
                          onChange={handleInputChange}
                          className="input-underline"
                          data-testid="checkout-city"
                        />
                      </div>
                      <div>
                        <Label htmlFor="postalCode" className="text-sm text-[#5D5550]">
                          Postal Code *
                        </Label>
                        <Input
                          id="postalCode"
                          name="postalCode"
                          required
                          value={formData.postalCode}
                          onChange={handleInputChange}
                          className="input-underline"
                          data-testid="checkout-postal"
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="country" className="text-sm text-[#5D5550]">
                        Country *
                      </Label>
                      <Select
                        value={formData.country}
                        onValueChange={handleCountryChange}
                      >
                        <SelectTrigger className="w-full" data-testid="checkout-country">
                          <SelectValue placeholder="Select your country" />
                        </SelectTrigger>
                        <SelectContent>
                          {countries.map((country) => (
                            <SelectItem key={country.value} value={country.value}>
                              {country.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                {/* Benefits */}
                <div className="bg-[#F2EFE9] p-6 space-y-4">
                  <div className="flex items-center gap-3 text-sm text-[#5D5550]">
                    <Truck className="h-5 w-5 text-[#C6A87C]" />
                    <span>
                      {subtotal >= freeShippingThreshold
                        ? 'You qualify for free worldwide shipping!'
                        : `Shipping: €${shippingCost.toFixed(2)} ${formData.country ? `to ${formData.country}` : '(select country)'}`}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 text-sm text-[#5D5550]">
                    <Shield className="h-5 w-5 text-[#C6A87C]" />
                    <span>Your payment is secured by Stripe</span>
                  </div>
                </div>

                <Button
                  type="submit"
                  className="btn-primary w-full"
                  disabled={loading || !formData.country}
                  data-testid="pay-now-btn"
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <div className="spinner w-4 h-4" />
                      Processing...
                    </span>
                  ) : (
                    <>
                      Pay Now — €{total.toFixed(2)}
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </>
                  )}
                </Button>
              </form>
            </motion.div>

            {/* Order Summary */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div
                className="bg-[#F2EFE9] p-6 md:p-8 sticky top-32"
                data-testid="checkout-summary"
              >
                <h2 className="font-heading text-xl text-[#2C2420] mb-6">
                  Order Summary
                </h2>

                {/* Items */}
                <div className="space-y-4 mb-6">
                  {items.map((item, index) => (
                    <div key={index} className="flex gap-4">
                      <div className="w-16 h-20 bg-[#F9F8F6] overflow-hidden flex-shrink-0">
                        <img
                          src={item.image}
                          alt={item.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm text-[#2C2420]">{item.name}</p>
                        <p className="text-xs text-[#8C8580]">
                          Qty: {item.quantity}
                          {item.size && ` · ${item.size}`}
                          {item.color && ` · ${item.color}`}
                        </p>
                        <p className="text-sm text-[#2C2420] mt-1">
                          €{(item.price * item.quantity).toFixed(2)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Totals */}
                <div className="border-t border-[#EBE5DE] pt-4 space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-[#5D5550]">Subtotal</span>
                    <span className="text-[#2C2420]">€{subtotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-[#5D5550]">
                      Shipping {formData.country && `(${formData.country})`}
                    </span>
                    <span className="text-[#2C2420]">
                      {shippingCost === 0 ? 'Free' : `€${shippingCost.toFixed(2)}`}
                    </span>
                  </div>
                  {subtotal < freeShippingThreshold && subtotal > 0 && (
                    <p className="text-xs text-[#C6A87C]">
                      Add €{(freeShippingThreshold - subtotal).toFixed(2)} more for free shipping
                    </p>
                  )}
                  <div className="flex justify-between pt-3 border-t border-[#EBE5DE]">
                    <span className="font-heading text-lg text-[#2C2420]">Total</span>
                    <span className="font-heading text-lg text-[#2C2420]">
                      €{total.toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </Layout>
  );
};
